import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import type { CotizacionWorldOffice } from '@/lib/supabase/tipos-bd'
import { mensajeDeError } from '@/lib/supabase/errores'

import { refrescarTodo } from './useDecidirCotizacion'

/**
 * ENLAZAR LA COTIZACIÓN DE WORLD OFFICE A UNA ORDEN.
 *
 * World Office es donde MEGA hace las cotizaciones de verdad, y el puente
 * entre las dos cosas es este enlace. Sin él la orden existe, el trabajo se
 * programa y sale la cuadrilla, pero el sistema no sabe cuánto vale: la ficha
 * dice «$ 0» y el tablero dice que no hay plata en juego.
 *
 * Las funciones de la base existían desde hace tiempo. Lo que no existía era
 * una pantalla que las llamara, así que en la práctica no se podía enlazar
 * nada: las veintitrés órdenes importadas del Excel llevaban semanas con una
 * cotización provisional en cero y no había por dónde arreglarlo.
 */

export type CandidataWorldOffice = {
  id_asiento: number
  numero_visible: string
  fecha: string
  cliente_nombre: string
  cliente_documento: string | null
  total: number | null
  es_del_cliente: boolean
  sugerida: boolean
  /** Negativo = la cotización es ANTERIOR a la visita. La pantalla lo avisa. */
  dias_despues: number | null
  /** Por qué se sugiere. Null cuando no se sugiere. */
  motivo: string | null
}

/**
 * Las candidatas para una orden.
 *
 * Arranca por las del mismo cliente, que es el caso normal. Se puede abrir a
 * todas porque el importador del Excel le inventó el documento a diez de los
 * veintitrés clientes —«SN-INGE-BRYAN», «SN-PAULA-REVUELTERIA»— y por
 * documento esos no cruzan con World Office ni cruzarán nunca.
 */
export function useCandidatasParaEnlazar(
  ordenServicioId: string | null,
  opciones: { soloDelCliente: boolean; busqueda: string },
) {
  const busqueda = opciones.busqueda.trim()

  return useQuery({
    queryKey: ['cotizaciones-para-enlazar', ordenServicioId, opciones.soloDelCliente, busqueda],
    enabled: Boolean(ordenServicioId),
    queryFn: async (): Promise<CandidataWorldOffice[]> => {
      // `enabled` ya garantiza que hay orden, pero el tipo no lo sabe y una
      // aserción a la brava taparía el día en que alguien quite el enabled.
      if (!ordenServicioId) return []

      const { data, error } = await supabase.rpc('fn_cotizaciones_para_enlazar', {
        p_orden_servicio_id: ordenServicioId,
        p_solo_del_cliente: opciones.soloDelCliente,
        p_busqueda: busqueda === '' ? null : busqueda,
        p_limite: 30,
      })

      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as CandidataWorldOffice[]
    },
  })
}

/**
 * Poner la cotización.
 *
 * La base adopta la provisional del import si la hay —no duplica el
 * documento— y no retrocede el estado: una orden ya aprobada, con gente en la
 * calle, no vuelve a «cotizada» porque le acabamos de poner el precio.
 */
export function useEnlazarCotizacion() {
  const cliente = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ordenServicioId,
      idAsiento,
      notas,
      adicional = false,
    }: {
      ordenServicioId: string
      idAsiento: number
      notas?: string | null
      /**
       * Adicional: materiales o labor que aparecieron a media obra. Se AÑADE
       * en vez de adoptar la que ya está, y no genera una segunda orden de
       * trabajo porque es la misma obra.
       */
      adicional?: boolean
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('fn_enlazar_cotizacion', {
        p_orden_servicio_id: ordenServicioId,
        p_wo_id_asiento: idAsiento,
        p_notas: notas ?? null,
        p_adicional: adicional,
      })

      if (error) throw new Error(mensajeDeError(error))
      return data as string
    },
    onSuccess: async () => {
      await refrescarTodo(cliente)
      // La lista de candidatas también cambia: la que se acaba de enlazar deja
      // de estar disponible para cualquier otra orden.
      await cliente.invalidateQueries({ queryKey: ['cotizaciones-para-enlazar'] })
      await cliente.invalidateQueries({ queryKey: ['adicionales'] })
      // Y la orden entera: su lista de documentos y su total cuelgan de aquí.
      await cliente.invalidateQueries({ queryKey: ['ordenes-servicio'] })
    },
  })
}

/* ── El sentido inverso ─────────────────────────────────────────────────────
   Desde la pantalla de cotizaciones el documento ya está delante —lo acabaron
   de hacer en World Office— y lo que falta es decir a qué orden va. Es la
   misma unión, con la misma función de escritura; solo cambia qué se busca. */

export type CandidataOrden = {
  orden_servicio_id: string
  numero: string
  cliente_nombre: string
  cliente_documento: string | null
  direccion: string | null
  servicio: string | null
  fecha_solicitud: string
  estado: string
  /** Trae la provisional del import, que el enlace va a adoptar. */
  tiene_provisional: boolean
  es_del_cliente: boolean
  sugerida: boolean
  motivo: string | null
}

export function useOrdenesParaEnlazar(idAsiento: number | null, busqueda: string) {
  const texto = busqueda.trim()

  return useQuery({
    queryKey: ['ordenes-para-enlazar', idAsiento, texto],
    enabled: idAsiento !== null,
    queryFn: async (): Promise<CandidataOrden[]> => {
      if (idAsiento === null) return []

      const { data, error } = await supabase.rpc('fn_ordenes_para_enlazar', {
        p_wo_id_asiento: idAsiento,
        p_busqueda: texto === '' ? null : texto,
        p_limite: 30,
      })

      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as CandidataOrden[]
    },
  })
}

/**
 * UNA COTIZACIÓN DE WORLD OFFICE, ENTERA.
 *
 * El buscador de candidatas devuelve lo justo para reconocerlas de un vistazo
 * —número, cliente, valor, fecha—, y eso basta para descartar, pero no para
 * DECIDIR: entre «la de 894.000» y «la de 451.600» del mismo cliente no hay
 * forma de saber cuál corresponde al trabajo sin ver qué lleva dentro.
 *
 * Se trae solo cuando alguien despliega una fila. Cargar los renglones de las
 * treinta candidatas por si acaso sería pedirle a la base treinta documentos
 * para mirar uno.
 */
export function useCotizacionWorldOffice(idAsiento: number | null) {
  return useQuery({
    queryKey: ['cotizacion-wo', idAsiento],
    enabled: idAsiento !== null,
    // Una cotización ya emitida no cambia: no hay por qué volver a pedirla al
    // cerrar y abrir la misma fila.
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CotizacionWorldOffice | null> => {
      if (idAsiento === null) return null

      const { data, error } = await supabase
        .from('vista_cotizaciones_world_office')
        .select('*')
        .eq('id_asiento', idAsiento)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))
      return (data ?? null) as unknown as CotizacionWorldOffice | null
    },
  })
}


/* ── Los adicionales de una orden ───────────────────────────────────────────
   Van aparte de la cotización principal a propósito: la principal es la que
   pone precio al trabajo y abrió la orden de trabajo; un adicional cuelga de
   esa misma obra. Mezclarlos en una sola cifra escondería de dónde salió la
   plata, que es justo lo que el cliente pregunta cuando ve el total. */

export type Adicional = {
  id: string
  numero_cotizacion: string | null
  estado: string
  valor_total: number | null
  fecha_cotizacion: string | null
  creado_en: string
  conceptos: number
}

export function useAdicionales(ordenServicioId: string | null) {
  return useQuery({
    queryKey: ['adicionales', ordenServicioId],
    enabled: Boolean(ordenServicioId),
    queryFn: async (): Promise<Adicional[]> => {
      if (!ordenServicioId) return []

      const { data, error } = await supabase.rpc('fn_adicionales_de_la_orden', {
        p_orden_servicio_id: ordenServicioId,
      })

      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as Adicional[]
    },
  })
}

/** Si a esta orden se le puede colgar uno: tiene obra andando. */
export function useAdmiteAdicional(ordenServicioId: string | null) {
  return useQuery({
    queryKey: ['admite-adicional', ordenServicioId],
    enabled: Boolean(ordenServicioId),
    queryFn: async (): Promise<boolean> => {
      if (!ordenServicioId) return false

      const { data, error } = await supabase.rpc('fn_admite_adicional', {
        p_orden_servicio_id: ordenServicioId,
      })

      if (error) return false
      return Boolean(data)
    },
  })
}
